import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { RuleService } from '@core/services/rule.service';
import * as RuleBuilderActions from './rule-builder.actions';
import { selectRuleFormData, selectRuleId, selectEditMode } from './rule-builder.selectors';

@Injectable()
export class RuleBuilderEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private ruleService = inject(RuleService);
  private router = inject(Router);

  /**
   * Initialize wizard - Load reference data (categories, templates, sheet names)
   */
  initializeWizard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleBuilderActions.initializeWizard),
      switchMap(() =>
        this.ruleService.getCategories().pipe(
          map(categories => RuleBuilderActions.loadCategoriesSuccess({ categories })),
          catchError(error =>
            of(RuleBuilderActions.initializeWizardFailure({ 
              error: 'Failed to load categories. Please refresh the page.' 
            }))
          )
        )
      )
    )
  );

  /**
   * Load templates when categories load successfully
   */
  loadTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleBuilderActions.loadCategoriesSuccess),
      switchMap(() =>
        this.ruleService.getTemplates().pipe(
          map(templates => RuleBuilderActions.loadTemplatesSuccess({ templates })),
          catchError(error =>
            of(RuleBuilderActions.initializeWizardFailure({ 
              error: 'Failed to load templates.' 
            }))
          )
        )
      )
    )
  );

  /**
   * Load rule for editing
   */
  loadRuleForEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleBuilderActions.loadRuleForEdit),
      switchMap(({ ruleId }) =>
        this.ruleService.getRuleById(ruleId).pipe(
          map(rule => RuleBuilderActions.loadRuleSuccess({ rule })),
          catchError(error =>
            of(RuleBuilderActions.loadRuleFailure({ 
              error: 'Failed to load rule. Please try again.' 
            }))
          )
        )
      )
    )
  );

  /**
   * Load template when selected
   */
  loadTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleBuilderActions.selectTemplate),
      switchMap(({ templateId }) =>
        this.ruleService.getTemplateById(templateId).pipe(
          map(template => RuleBuilderActions.loadTemplateDetailsSuccess({ template })),
          catchError(error =>
            of(RuleBuilderActions.loadTemplateDetailsFailure({ 
              error: 'Failed to load template.' 
            }))
          )
        )
      )
    )
  );

  /**
   * Create rule from template
   */
  createFromTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleBuilderActions.createRuleFromTemplate),
      withLatestFrom(
        this.store.select(selectRuleFormData)
      ),
      switchMap(([{ templateId }, formData]) =>
        this.ruleService.createRuleFromTemplate({
          templateId,
          name: formData.name || 'New Rule from Template',
          description: formData.description,
          targetSheets: formData.targetSheets || []
        }).pipe(
          map(rule => RuleBuilderActions.createRuleFromTemplateSuccess({ rule })),
          catchError(error =>
            of(RuleBuilderActions.createRuleFromTemplateFailure({ 
              error: 'Failed to create rule from template.' 
            }))
          )
        )
      )
    )
  );

  /**
   * Parse Excel formula
   */
  parseFormula$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleBuilderActions.parseFormula),
      switchMap(({ formula }) =>
        this.ruleService.parseFormula({ formula }).pipe(
          map(result => RuleBuilderActions.parseFormulaSuccess({ sources: result.sources })),
          catchError(error =>
            of(RuleBuilderActions.parseFormulaFailure({ 
              error: 'Failed to parse formula. Please check the syntax.' 
            }))
          )
        )
      )
    )
  );

  /**
   * Test rule when file is uploaded
   */
  uploadTestFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleBuilderActions.uploadTestFile),
      withLatestFrom(
        this.store.select(selectRuleFormData)
      ),
      switchMap(([{ file }, formData]) =>
        this.ruleService.testRule({
          ruleType: formData.ruleType || '',
          configuration: formData.configuration || {},
          targetSheets: formData.targetSheets || []
        }, file).pipe(
          map(results => RuleBuilderActions.testRuleSuccess({ results })),
          catchError(error =>
            of(RuleBuilderActions.testRuleFailure({ 
              error: error.message || 'Failed to test rule. Please try again.' 
            }))
          )
        )
      )
    )
  );



  /**
   * Save rule (create or update)
   */
  saveRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleBuilderActions.saveRule),
      withLatestFrom(
        this.store.select(selectRuleFormData),
        this.store.select(selectRuleId),
        this.store.select(selectEditMode)
      ),
      switchMap(([_, formData, ruleId, editMode]) => {
        // Ensure required fields are valid for API
        // configuration must be a JSON string per UpdateRuleDTO / CreateRuleDTO
        const ruleData: any = {
          ...formData,
          ruleType: formData.ruleType || '',
          configuration: JSON.stringify(formData.configuration || {}),
          targetSheets: formData.targetSheets || [],
          name: formData.name || 'Untitled Rule',
          description: formData.description || '',
          isActive: formData.isActive ?? true,
          categoryId: formData.category  // store uses 'category' key; API expects 'categoryId'
        };
        
        const request$ = editMode && ruleId
          ? this.ruleService.updateRule(ruleId, ruleData)
          : this.ruleService.createRule(ruleData);

        return request$.pipe(
          map(rule => RuleBuilderActions.saveRuleSuccess({ rule })),
          catchError(error =>
            of(RuleBuilderActions.saveRuleFailure({ 
              error: error.message || 'Failed to save rule. Please try again.' 
            }))
          )
        );
      })
    )
  );

  /**
   * Navigate to rules list on successful save
   */
  saveRuleSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RuleBuilderActions.saveRuleSuccess),
        tap(() => {
          this.router.navigate(['/rules']);
        })
      ),
    { dispatch: false }
  );

  /**
   * Navigate back on cancel
   */
  cancelWizard$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RuleBuilderActions.cancelWizard),
        tap(() => {
          this.router.navigate(['/rules']);
        })
      ),
    { dispatch: false }
  );
}
